import React, { useState, useEffect } from "react";
import { TextField, makeStyles, createStyles, Theme, MenuItem } from "@material-ui/core";
import { EventType } from "pages/statuses/EventType";
import { BookingApi } from "api/BookingApi";
import { ConfigGetter } from "utilities/ConfigGetter";
import * as ConfigKey from "utilities/ConfigGetter";
import { Faculty } from "pages/statuses/Faculty";
import { Department } from "pages/statuses/Department";
import { Laboratory } from "pages/statuses/Laboratory";
import { BookingConfirmItems } from "pages/booking/dialogParts/BookingConfirmItems";
import { UserApi } from "api/UserApi";
import { UserDetail } from "api/entities/UserDetail";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        form: {
            margin: theme.spacing(5),
            marginBottom: theme.spacing(1),
        },
        email: {
            "& .MuiTextField-root": {
                width: `50%`,
            },
        },
    })
);

interface Props {
    // 予約情報
    booking: EventType;
    // ローディング状態のセッター
    setLoading: any;
}

/**
 * 予約登録コンポーネント
 * 予約者情報を登録するコンポーネント
 * @param props
 */
export const SubscribeParts: React.FC<Props> = (props) => {
    const { booking, setLoading } = props;
    const classes = useStyles();
    const [faculties, setFaculties] = useState<Faculty[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [laboratories, setLaboratories] = useState<Laboratory[]>([]);
    const emailDomainList: string[] = ConfigGetter.getList(ConfigKey.EMAIL_DOMAIN_LIST);

    /**
     * 指定した学部情報から、選択状態の学部名を取得する
     * 合致する学部名がない場合は、空文字を返却する
     */
    const getSubscriberFaculty = (faculties: Faculty[]): string => {
        const faculty = faculties.find((item) => {
            return item.faculty === booking.subscriberFaculty;
        });
        return faculty?.faculty || ``;
    };
    const [inputSubscriberFaculty, setInputSubscriberFaculty] = useState<string>(getSubscriberFaculty(faculties));

    /**
     * 指定した学科情報から、選択状態の学科名を取得する
     * 合致する学科名がない場合は、空文字を返却する
     */
    const getSubscriberDepartment = (departments: Department[]): string => {
        const department = departments.find((item) => {
            return item.department === booking.subscriberDepartment;
        });
        return department?.department || ``;
    };
    const [inputSubscriberDepartment, setInputSubscriberDepartment] = useState<string>(
        getSubscriberDepartment(departments)
    );

    /**
     * 指定した研究室情報から、選択状態の研究室名を取得する
     * 合致する研究室名がない場合は、空文字を返却する
     */
    const getSubscriberLaboratoryName = (laboratories: Laboratory[]): string => {
        const laboratory = laboratories.find((item) => {
            return (
                item.laboratoryId === booking.subscriberLaboratoryId &&
                item.laboratoryName === booking.subscriberLaboratoryName
            );
        });
        return laboratory?.laboratoryName || ``;
    };
    const [inputSubscriberLaboratoryName, setInputSubscriberLaboratoryName] = useState<string>(
        getSubscriberLaboratoryName(laboratories)
    );

    const [inputSubscriberName, setInputSubscriberName] = useState<string>(
        booking.subscriberName !== undefined ? booking.subscriberName : ``
    );

    /**
     * メールアドレスをローカル部とドメイン部に分割する
     * メールアドレスが未設定の場合、ドメイン部はデフォルトを返却する
     */
    const getSeparateMailAddress = (mailAddress: string | undefined): { local: string; domain: string } => {
        if (mailAddress === undefined) {
            return {
                local: ``,
                domain: emailDomainList[0],
            };
        }
        const domain = emailDomainList.find((item) => item.replace(`@`, ``) === mailAddress.split(`@`)[1]);
        return {
            local: mailAddress.split(`@`)[0] || ``,
            domain: domain || emailDomainList[0],
        };
    };
    /**
     * メールアドレスをローカル部とドメイン部を結合する
     * ローカル部が空の場合、空文字を返却する
     */
    const getSubscriberMailAddress = (local: string, domain: string): string => {
        if (local === ``) return ``;
        return `${local}${domain}`;
    };
    const mailAddress = getSeparateMailAddress(booking.subscriberMailAddress);
    const [inputLocalMailAddress, setInputLocalMailAddress] = useState<string>(mailAddress.local);
    const [inputDomainMailAddress, setInputDomainMailAddress] = useState<string>(mailAddress.domain);
    const [inputSubscriberTel, setInputSubscriberTel] = useState<string | undefined>(booking.subscriberTel);
    const [inputSubscriberMemo, setInputSubscriberMemo] = useState<string | undefined>(booking.subscriberMemo);

    /**
     * 指定された値が空かどうか
     * undefined、null、空文字の場合trueを返却する
     */
    const isEmpty = (value: any) => {
        return value === undefined || value === null || value === ``;
    };

    useEffect(() => {
        const execute = async () => {
            // ローディング開始
            setLoading(true);

            // 全ての研究室情報を取得
            const getUnivercityBasicInfoResponse = await BookingApi.getUnivercityBasicInfo();
            if (getUnivercityBasicInfoResponse?.status === 200) {
                const faculties: Faculty[] = Array.from(getUnivercityBasicInfoResponse?.data.faculties);
                const departments: Department[] = Array.from(getUnivercityBasicInfoResponse?.data.departments);
                const laboratories: Laboratory[] = Array.from(getUnivercityBasicInfoResponse?.data.laboratories);

                // 学部設定
                setFaculties(faculties);
                // 学科設定
                setDepartments(departments);
                // 研究室設定
                setLaboratories(laboratories);

                // 初期表示時はユーザー情報を表示、編集時は編集中の情報を表示
                if (
                    isEmpty(booking.subscriberFaculty) ||
                    isEmpty(booking.subscriberDepartment) ||
                    isEmpty(booking.subscriberLaboratoryId)
                ) {
                    const getUserDetailResponse = await UserApi.getUserDetail();
                    if (getUserDetailResponse?.status === 200) {
                        const user: UserDetail = getUserDetailResponse?.data;
                        setInputSubscriberFaculty(user.faculty);
                        booking.subscriberFaculty = user.faculty;
                        setInputSubscriberDepartment(user.department);
                        booking.subscriberDepartment = user.department;
                        setInputSubscriberLaboratoryName(user.laboName);
                        booking.subscriberLaboratoryId = user.laboId;
                        booking.subscriberLaboratoryName = user.laboName;
                    }
                } else {
                    const faculty = getSubscriberFaculty(faculties);
                    setInputSubscriberFaculty(faculty);
                    const department = getSubscriberDepartment(departments);
                    setInputSubscriberDepartment(department);
                    const laboratoryName = getSubscriberLaboratoryName(laboratories);
                    setInputSubscriberLaboratoryName(laboratoryName);
                }
            }
            // ローディング終了
            setLoading(false);
        };
        execute();
        // eslint-disable-next-line
    }, []);

    return (
        <div className={classes.form}>
            <BookingConfirmItems booking={booking} />
            <div>
                【入力フォーム】
                <TextField
                    margin="dense"
                    id="faculty"
                    label="学部"
                    type="text"
                    value={inputSubscriberFaculty}
                    fullWidth
                    inputProps={{
                        readOnly: true,
                    }}
                />
                <TextField
                    margin="dense"
                    id="department"
                    label="学科"
                    type="text"
                    value={inputSubscriberDepartment}
                    fullWidth
                    inputProps={{
                        readOnly: true,
                    }}
                />
                <TextField
                    margin="dense"
                    id="laboratory"
                    label="研究室名"
                    value={inputSubscriberLaboratoryName + " 研究室"}
                    fullWidth
                    inputProps={{
                        readOnly: true,
                    }}
                />
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    label="担当者"
                    type="text"
                    value={inputSubscriberName}
                    onChange={(e) => {
                        booking.subscriberName = e.target.value;
                        setInputSubscriberName(e.target.value);
                    }}
                    fullWidth
                />
                <div className={classes.email}>
                    <TextField
                        margin="dense"
                        id="email"
                        label="メールアドレス"
                        type="email"
                        value={inputLocalMailAddress}
                        onChange={(e) => {
                            setInputLocalMailAddress(e.target.value);
                            booking.subscriberMailAddress = getSubscriberMailAddress(
                                e.target.value,
                                inputDomainMailAddress
                            );
                            setInputLocalMailAddress(e.target.value);
                        }}
                    />
                    <TextField
                        select
                        margin="dense"
                        id="email_domain"
                        label="ドメイン"
                        value={inputDomainMailAddress}
                        onChange={(e) => {
                            booking.subscriberMailAddress = getSubscriberMailAddress(
                                inputLocalMailAddress,
                                e.target.value
                            );
                            setInputDomainMailAddress(e.target.value);
                        }}
                    >
                        {emailDomainList.map((domain, index) => {
                            return (
                                <MenuItem key={`${domain}${index}`} value={domain}>
                                    {domain}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                </div>
                <TextField
                    margin="dense"
                    id="tel"
                    label="内線"
                    type="text"
                    value={inputSubscriberTel}
                    onChange={(e) => {
                        booking.subscriberTel = e.target.value;
                        setInputSubscriberTel(e.target.value);
                    }}
                    fullWidth
                />
                <TextField
                    margin="dense"
                    id="memo"
                    label="備考"
                    type="text"
                    value={inputSubscriberMemo}
                    onChange={(e) => {
                        booking.subscriberMemo = e.target.value;
                        setInputSubscriberMemo(e.target.value);
                    }}
                    fullWidth
                />
            </div>
        </div>
    );
};
